import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import {
  alignVariants,
  gapVariants,
  justifyVariants,
  type LayoutAlign,
  type LayoutJustify,
  type LayoutWrap,
  type SpacingToken,
  wrapVariants,
} from "./shared";

const groupVariants = cva("flex flex-row", {
  variants: {
    align: alignVariants,
    justify: justifyVariants,
    gap: gapVariants,
    wrap: wrapVariants,
  },
  defaultVariants: {
    gap: 8,
    align: "center",
  },
});

export interface GroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  /** Gap between children, maps to --spacing-{n}. Defaults to 8. */
  gap?: SpacingToken;
  /** Alignment of items on the vertical axis. Defaults to "center". */
  align?: LayoutAlign;
  /** Justification of items on the horizontal axis */
  justify?: LayoutJustify;
  /** Whether children should wrap */
  wrap?: LayoutWrap;
  /** Whether children should grow to fill available space equally */
  grow?: boolean;
  children?: React.ReactNode;
}

/**
 * Horizontal flex layout with tokenized gap.
 *
 * @example
 * <Group justify="between">
 *   <Title size="md">Enclosures</Title>
 *   <Button variant="outline">Add New</Button>
 * </Group>
 */
function Group({
  gap,
  align,
  justify,
  wrap,
  grow = false,
  children,
  ...props
}: GroupProps) {
  return (
    <div
      className={cn(
        groupVariants({ gap, align, justify, wrap }),
        grow && "[&>*]:flex-1",
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export { Group };
